import { SearchIcon, XIcon } from "lucide-react";
import { useContext, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/field";
import { Loader } from "@/components/ui/loader";
import { Text } from "@/components/ui/text";
import { useTinyKeys } from "@/lib/useTinyKeys";
import { useNavSelected } from "@/utils/useNavStack";
import { useSearchQuery } from "../store";
import { AccessionSearchContext } from "./SearchProvider";

export function SearchInput() {
  const [queryStr, setQueryStr] = useSearchQuery();
  const [value, setValue] = useState(() => queryStr);
  const { isLoading } = useContext(AccessionSearchContext);
  const ref = useRef<HTMLInputElement>(null);
  const { selected, reset: resetSelected } = useNavSelected();

  useEffect(() => {
    if (queryStr) {
      setValue(queryStr);
    }
  }, [queryStr]);

  useTinyKeys(
    {
      Escape: () => {
        if (queryStr) {
          setQueryStr("");
          resetSelected();
        }
        setValue("");
      },
    },
    [queryStr],
  );

  return (
    <div className="flex items-center w-full [&>span:first-child]:focus-within:text-black">
      {isLoading ? (
        <Loader />
      ) : (
        <Text muted className="inline-block w-4">
          <SearchIcon className="size-4" />
        </Text>
      )}
      <Input
        autoFocus={!selected}
        ref={ref}
        value={value}
        onChange={(evt) => setValue(evt.currentTarget.value)}
        placeholder="Search accessions..."
        className="border-none shadow-none ring-0 outline-0 focus:ring-0"
        onKeyDown={async (evt) => {
          if (evt.key === "Enter") {
            setQueryStr(value);
          }
          if (evt.key === "ArrowDown") {
            evt.preventDefault();
            const listitem = document.querySelector("[role='listitem']");
            if (listitem) {
              (listitem as HTMLElement)?.focus?.();
            }
          }
        }}
      />
      {value && (
        <Button
          variant="ghost"
          size="icon"
          excludeFromTabOrder
          onPress={() => {
            setValue("");
            setQueryStr("");
          }}
        >
          <XIcon />
        </Button>
      )}
    </div>
  );
}
